import React, { useEffect, useState } from 'react';
import {
	ExclamationTriangleIcon,
	ClipboardDocumentListIcon,
	CalendarDaysIcon,
} from '@heroicons/react/24/outline';
import { StackableModal } from '../components/nextgen';
import useModal from '../hooks/useModal';
import useAsync from '../hooks/useAsync';
import { getDeletePreview, deleteContractWithOptions } from '../api/contracts';

const WORK_ORDER_OPTIONS = [
	{
		value: 'leave',
		label: 'Leave as-is',
		description: 'Keep work orders in their current state',
	},
	{
		value: 'close',
		label: 'Cancel all',
		description: 'Mark all open work orders as cancelled',
	},
	{
		value: 'delete',
		label: 'Delete all',
		description: 'Remove all open work orders from the system',
	},
];

const PM_SCHEDULE_OPTIONS = [
	{
		value: 'keep',
		label: 'Keep active',
		description: 'PM schedules will continue to generate work orders',
	},
	{
		value: 'delete',
		label: 'Deactivate',
		description: 'Stop PM schedules from generating new work orders',
	},
];

const RadioOption = ({ option, selected, onChange, name }) => (
	<label
		className={`relative flex cursor-pointer rounded-lg border p-4 focus:outline-none ${
			selected
				? 'border-indigo-600 ring-2 ring-indigo-600'
				: 'border-gray-300 hover:border-gray-400'
		}`}
	>
		<input
			type="radio"
			name={name}
			value={option.value}
			checked={selected}
			onChange={() => onChange(option.value)}
			className="sr-only"
		/>
		<span className="flex flex-1">
			<span className="flex flex-col">
				<span
					className={`block text-sm font-medium ${
						selected ? 'text-indigo-900' : 'text-gray-900'
					}`}
				>
					{option.label}
				</span>
				<span className="mt-1 flex items-center text-sm text-gray-500">
					{option.description}
				</span>
			</span>
		</span>
		<span
			className={`pointer-events-none absolute -inset-px rounded-lg border-2 ${
				selected ? 'border-indigo-600' : 'border-transparent'
			}`}
			aria-hidden="true"
		/>
	</label>
);

const DeleteContractModal = () => {
	const { open, setClose } = useModal('deleteContract');
	const [workOrderAction, setWorkOrderAction] = useState('leave');
	const [pmScheduleAction, setPmScheduleAction] = useState('keep');
	const [isDeleting, setIsDeleting] = useState(false);

	const {
		value: preview,
		status: previewStatus,
		execute: fetchPreview,
	} = useAsync(getDeletePreview, { immediate: false });

	const { execute: onDeleteContract } = useAsync(deleteContractWithOptions, {
		immediate: false,
		showNotification: { success: 'Contract has been deleted', error: 'raw' },
	});

	useEffect(() => {
		if (open?.contractId) {
			fetchPreview({ id: open.contractId });
			// Reset selections when modal opens
			setWorkOrderAction('leave');
			setPmScheduleAction('keep');
		}
	}, [open?.contractId]);

	const handleDelete = async () => {
		setIsDeleting(true);
		try {
			await onDeleteContract(
				{
					id: open.contractId,
					workOrderAction,
					pmScheduleAction,
				},
				() => {
					setClose();
					if (open?.onSuccess) {
						open.onSuccess();
					}
				},
			);
		} finally {
			setIsDeleting(false);
		}
	};

	const isLoading = previewStatus === 'pending' || previewStatus === 'idle';
	const hasOpenWorkOrders = preview?.openWorkOrders?.count > 0;
	const hasActiveSchedules = preview?.activeSchedules?.count > 0;

	return (
		<StackableModal open={!!open} onClose={() => setClose()} size="xl">
			<div>
				{isLoading ? (
					<div className="flex items-center justify-center py-12">
						<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
					</div>
				) : (
					<>
						{/* Warning Header */}
						<div className="flex items-start gap-4 mb-6 bg-red-50/50 rounded-xl px-4 py-6 px-10">
							<div>
								<h3 className="text-lg font-semibold text-gray-900">
									Are you sure you want to delete this contract?
								</h3>
								<p className="mt-1 text-xs text-gray-500">
									Deleting{' '}
									<span className="font-medium">{preview?.contract?.name}</span>{' '}
									will permanently remove it. This action cannot be undone.
								</p>
							</div>
						</div>

						{/* Work Orders Section */}
						{hasOpenWorkOrders && (
							<div className="mb-6 px-10">
								<div className="flex items-center gap-2 mb-3">
									<ClipboardDocumentListIcon className="h-5 w-5 text-amber-500" />
									<h4 className="text-sm font-medium text-gray-900">
										Open Work Orders ({preview.openWorkOrders.count})
									</h4>
								</div>
								<p className="text-sm text-gray-500 mb-3">
									This contract has {preview.openWorkOrders.count} open work
									order{preview.openWorkOrders.count !== 1 ? 's' : ''}. What
									would you like to do with them?
								</p>
								<div className="grid gap-3">
									{WORK_ORDER_OPTIONS.map((option) => (
										<RadioOption
											key={option.value}
											option={option}
											selected={workOrderAction === option.value}
											onChange={setWorkOrderAction}
											name="workOrderAction"
										/>
									))}
								</div>
							</div>
						)}

						{/* PM Schedules Section */}
						{hasActiveSchedules && (
							<div className="mb-6 px-10">
								<div className="flex items-center gap-2 mb-3">
									<CalendarDaysIcon className="h-5 w-5 text-blue-500" />
									<h4 className="text-sm font-medium text-gray-900">
										Active PM Schedules ({preview.activeSchedules.count})
									</h4>
								</div>
								<p className="text-sm text-gray-500 mb-3">
									This contract has {preview.activeSchedules.count} active PM
									schedule{preview.activeSchedules.count !== 1 ? 's' : ''}. What
									would you like to do with them?
								</p>
								<div className="grid gap-3">
									{PM_SCHEDULE_OPTIONS.map((option) => (
										<RadioOption
											key={option.value}
											option={option}
											selected={pmScheduleAction === option.value}
											onChange={setPmScheduleAction}
											name="pmScheduleAction"
										/>
									))}
								</div>
							</div>
						)}

						{/* No related items message */}
						{!hasOpenWorkOrders && !hasActiveSchedules && (
							<div className="mb-6 p-4 bg-gray-50 rounded-lg">
								<p className="text-sm text-gray-600">
									This contract has no open work orders or active PM schedules.
								</p>
							</div>
						)}
					</>
				)}

				{/* Action Buttons */}
				<div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
					<button
						type="button"
						onClick={() => setClose()}
						className="rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
					>
						Cancel
					</button>
					<button
						type="button"
						onClick={handleDelete}
						disabled={isLoading || isDeleting}
						className="rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
					>
						{isDeleting ? 'Deleting...' : 'Delete Contract'}
					</button>
				</div>
			</div>
		</StackableModal>
	);
};

export default DeleteContractModal;
